import { CalloutComponent, FormValues, getValue, InputFormCallout, InputFormGroup } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { StorageSizeSelect } from "../../components/StorageSizeSelect/StorageSizeSelect";
import { CalloutTestIds } from "../../constants/uiConstants";
import { VmdbDbSystemOptionsStorageManagementEnum } from "../../gen/clients/mchub-azure-api-client-vmdb";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_DB_SYSTEM_UPDATE_POLL_DELAY,
  VMDB_DB_SYSTEM_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export enum Fields {
  StorageSize = "storageSize",
}

export enum FieldTestIds {
  StorageSize = "storage-size-field",
}

export interface VmDbStorageConfig {
  storageManagement: VmdbDbSystemOptionsStorageManagementEnum | undefined;
  dataStorageSizeInGBs: string | undefined;
}

export interface VmDbSystemEditStorageProps extends OperationActionProps {
  dbSystemId: string | undefined;
  storageConfig: VmDbStorageConfig;
}

const VmDbSystemEditStorage = (
  // eslint-disable-next-line max-len
  { targetId, location, dbSystemId, storageConfig, onCancel, onExecute }: VmDbSystemEditStorageProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(dbSystemId);
  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");

  const { invokeCall: invokeUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.updateVmdbDbSystem },
  );

  const [storageCalloutRef, setStorageCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const onEditStorage = (formValues: FormValues): void => {
    const systemDataStorageSizeInGBs = getValue(formValues, Fields.StorageSize, InputFormGroup)?.[0].id;
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: storageCalloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateVmDbSystemStorage(),
          message: Messages.notifications.inProgress.messages.updateVmDbSystemStorage(resName),
        },
        success: {
          title: Messages.notifications.success.titles.updateVmDbSystemStorage(),
          message: Messages.notifications.success.messages.updateVmDbSystemStorage(resName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateVmDbSystemStorage(),
          message: Messages.notifications.failure.messages.updateVmDbSystemStorage(resName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.VMDB_DB_SYSTEM_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            dbSystemName: resName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.VMDB_DB_SYSTEM_UPDATED_CHECK,
          delay: VMDB_DB_SYSTEM_UPDATE_POLL_DELAY,
          interval: VMDB_DB_SYSTEM_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeUpdate({
      dbSystemName: resName,
      resourceGroupName: resGroup,
      subscriptionId,
      updateDbSystemDetails: { dataStorageSizeInGBs: systemDataStorageSizeInGBs },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      testId={CalloutTestIds.VmDbSystemEditStorage}
      componentRef={setStorageCalloutRef}
      targetId={targetId}
      title={Messages.createNewPanels.editStorage.title()}
      onSubmit={onEditStorage}
      onClose={onCancel}
    >
      <StorageSizeSelect
        required
        testId={FieldTestIds.StorageSize}
        label={Messages.labels.availableDataStorageGB()}
        fieldName={Fields.StorageSize}
        storageType={storageConfig.storageManagement}
        defaultValue={storageConfig.dataStorageSizeInGBs ? [storageConfig.dataStorageSizeInGBs] : undefined}
      />
    </InputFormCallout>
  );
};

export const newVmDbSystemEditStorage = (
  props: VmDbSystemEditStorageProps,
): JSX.Element => (<VmDbSystemEditStorage {...props} />);
